// Uploads go straight to the bucket (a URL the API authorizes), so the editor is the only place an image can be
// made lighter: a photo straight from a phone is 3–5 MB and 4000 px wide, and every visitor would download it

/** The longest side an uploaded image keeps (wider than any place a Keepo shows one, even on retina screens). */
export const MAX_SIDE = 2000;
const QUALITY = 0.85;

/** The size within `maxSide` on the longest side, keeping the proportion (never larger than the original). */
export const fitWithin = (width: number, height: number, maxSide: number) => {
  const scale = Math.min(1, maxSide / Math.max(width, height));
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
};

const loadImage = (file: File): Promise<HTMLImageElement | null> =>
  new Promise(resolve => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });

const toBlob = (canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> =>
  new Promise(resolve => canvas.toBlob(resolve, type, quality));

const hasTransparency = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
  const { data } = ctx.getImageData(0, 0, width, height);
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) return true;
  }
  return false;
};

const EXTENSIONS: Record<string, string> = { "image/jpeg": "jpg", "image/webp": "webp", "image/png": "png" };

const renamed = (name: string, type: string) => `${name.replace(/\.[^./]+$/, "") || "imagem"}.${EXTENSIONS[type]}`;

const readStart = (file: File, bytes: number): Promise<Uint8Array> =>
  new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => resolve(new Uint8Array());
    reader.readAsArrayBuffer(file.slice(0, bytes));
  });

// one character per byte (a spread of 64 KB into String.fromCharCode passes Safari's argument limit)
const ascii = (data: Uint8Array, from: number, length: number) =>
  new TextDecoder("latin1").decode(data.subarray(from, from + length));

/** An animated PNG (an acTL chunk before the image data) or WebP (VP8X with the animation flag): a canvas keeps only its first frame. */
const isAnimated = async (file: File) => {
  if (file.type !== "image/png" && file.type !== "image/webp") return false;
  const data = await readStart(file, 64 * 1024);
  if (ascii(data, 1, 3) === "PNG") {
    const text = ascii(data, 0, data.length);
    const actl = text.indexOf("acTL");
    const idat = text.indexOf("IDAT");
    return actl !== -1 && (idat === -1 || actl < idat);
  }
  return ascii(data, 0, 4) === "RIFF" && ascii(data, 8, 8) === "WEBPVP8X" && (data[20] & 0x02) !== 0;
};

/**
 * The image made lighter before it's uploaded: at most MAX_SIDE px on the longest side, a JPEG when it has no
 * transparency, a WebP when it has (a PNG where the browser can't make WebP). A GIF, an animated PNG / WebP, an SVG,
 * an image the browser can't read, one that would come out heavier, or any failure on the way (low memory, a
 * blocked canvas) goes as it is: compressing never stops an upload.
 */
export async function compressImage(file: File): Promise<File> {
  if (!file.type.startsWith("image/") || file.type === "image/gif" || file.type === "image/svg+xml") {
    return file;
  }
  const canvas = document.createElement("canvas");
  try {
    if (await isAnimated(file)) {
      return file;
    }
    const img = await loadImage(file);
    if (!img) {
      return file;
    }
    const { width, height } = fitWithin(img.naturalWidth, img.naturalHeight, MAX_SIDE);
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx || !width || !height) {
      return file;
    }
    ctx.drawImage(img, 0, 0, width, height);

    const transparent = file.type !== "image/jpeg" && hasTransparency(ctx, width, height);
    const blob = await toBlob(canvas, transparent ? "image/webp" : "image/jpeg", QUALITY);
    if (!blob || !EXTENSIONS[blob.type] || blob.size >= file.size) {
      return file;
    }

    return new File([blob], renamed(file.name, blob.type), { type: blob.type });
  } catch {
    return file;
  } finally {
    // Safari keeps a canvas's memory until it's resized: several uploads in a row would add up
    canvas.width = 0;
    canvas.height = 0;
  }
}
